import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Mail, Phone } from 'lucide-react';
import PollHeaderBar from './PollHeaderBar';
import useSecondaryOf from '../utils/useSecondaryOf';
import SettingsModal from './SettingsModal';
import SharedStatusToast from './StatusToast';

// ---------------------------------------------------------------------------
// Calendar Hub — Phase 1/2 Poll Page
// Built 17 Aug 2026, replacing poll-ui-mockup-V14_08_3.jsx's BASELINE_OFFERINGS /
// BASELINE_ATTENDANCE fixtures with the three real endpoints built 16 Aug:
//   GET  /api/events/:eventId/polls/current
//   GET  /api/polls/:pollId/state
//   POST /api/polls/:pollId/responses/bulk
//
// Everything the mockup hardcoded (event details, course list, "already
// offered" names, attendance totals) is now driven entirely from what the
// API actually returns — course tabs come from the dish_outline questions'
// config.course, attendance options come from the attending question's
// config.options if present (falling back to sensible defaults per phase),
// so this doesn't assume a schema shape beyond what getPollState documents.
//
// Phase 2's "Cooking requirements" freeform field: one shared
// 'cooking_requirements' question per confirm poll, created by
// closeOfferPollAndAdvance (pollsService.js) when anyone offered a dish.
// Each member's answer is their own event_poll_responses row, shown back
// to everyone on PollResultsPage.jsx under that member's contribution
// (24 Sep 2026).
// ---------------------------------------------------------------------------

function Stepper({ value, onChange, min = 1, max = 12 }) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-10 h-10 rounded-full border border-stone-300 text-lg font-medium text-stone-700 active:bg-stone-100 flex items-center justify-center"
        aria-label="Decrease"
      >
        –
      </button>
      <span className="w-8 text-center text-lg font-semibold text-stone-800">{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        className="w-10 h-10 rounded-full border border-stone-300 text-lg font-medium text-stone-700 active:bg-stone-100 flex items-center justify-center"
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );
}

// (21 Oct 2026, 8.42, decisions 105, 113) A date in full, e.g. "Sunday 25 October", read
// from YYYY-MM-DD as it is, so no time zone can shift the day.
const fullDayDate = (isoDate) => {
  const iso = String(isoDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
};

function SectionLabel({ children }) {
  return <p className="text-xs uppercase tracking-wide text-stone-600 mb-2">{children}</p>;
}

// (3 Oct 2026) The member's own saved answer, in words (an uncoloured pair
// of buttons was the only sign of "not answered yet"). In Phase 2 a Phase 1
// Yes/No is carried over but not yet confirmed until the member saves.
// who = the member's name in proxy mode, else null ("You").
function MyAnswerLine({ saved, hasRespondedThisPhase, isConfirmPhase, who, verb }) {
  const value = saved && saved.value;
  const party = value === 'Yes' && saved.follow_up_value ? ` (party of ${Number(saved.follow_up_value) || 1})` : '';
  const subject = who || 'You';
  const possessive = who ? `${who}'s` : 'Your';
  let text;
  let tone = 'text-stone-600';
  if (!value) {
    text = isConfirmPhase
      ? `${subject} ${who ? (verb === 'are' ? "haven't" : "hasn't") : "haven't"} confirmed yet.`
      : `${subject} ${who ? (verb === 'are' ? "haven't" : "hasn't") : "haven't"} answered yet.`;
    tone = 'text-rose-700';
  } else if (isConfirmPhase && !hasRespondedThisPhase) {
    text = `${possessive} Phase 1 answer, ${value}${party}, is carried over but not yet confirmed.`;
    tone = 'text-amber-700';
  } else {
    text = `${possessive} answer: ${value}${party}.`;
  }
  return <p className={`text-sm font-medium mb-3 ${tone}`}>{text}</p>;
}

function byPreferredName(a, b) {
  return (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' });
}

// (3 Oct 2026, Neil) Live results while the poll is open: names of those
// coming (people decide partly on who else is going); No / Not sure as
// counts only; "N of M have answered" without naming who hasn't. Phase 2
// separates confirmed members from Phase 1 Yeses not yet confirmed.
// (3 Oct 2026, Neil) The viewer's own line reads "You" (myMemberId; null in
// proxy mode, where the administrator is answering for someone else).
// (26 Oct 2026, decision 144) In Phase 2 the attendee count is split as on the results
// page: "Attendees: 9 confirmed · 2 not yet confirmed"; a member confirmed by the
// administrator reads "· confirmed by {name}".
function LiveResultsBox({ totals, headcount, confirmedHeadcount, unconfirmedHeadcount, live, isConfirmPhase, closesOnText, myMemberId }) {
  const yes = (live?.attendees || []).filter((a) => a.answer === 'Yes');
  const confirmed = yes.filter((a) => !isConfirmPhase || a.confirmed).sort(byPreferredName);
  const carried = isConfirmPhase ? yes.filter((a) => !a.confirmed).sort(byPreferredName) : [];
  const list = (people) => (
    <ul className="space-y-0.5 mt-1">
      {people.map((a) => (
        <li key={a.memberId} className="text-sm text-stone-700">
          {myMemberId && a.memberId === myMemberId ? 'You' : a.name}{a.partySize != null && <span className="text-stone-500"> (party of {a.partySize})</span>}
          {isConfirmPhase && a.confirmed && a.confirmedBy && <span className="text-xs text-stone-500 inline-block whitespace-nowrap">&nbsp;· confirmed by {a.confirmedBy}</span>}
        </li>
      ))}
    </ul>
  );
  return (
    <div className="rounded-lg bg-stone-50 border border-stone-200 p-3">
      <p className="text-xs uppercase tracking-wide text-stone-600 mb-1">Answers so far — not final</p>
      <p className="text-xs text-stone-500 mb-2">
        {/* (21 Oct 2026, 8.42, decisions 105, 113) The closing time and the date in full. */}
        Members can still answer or change their answer until the poll closes{closesOnText ? ` at 7 a.m. on ${closesOnText}` : ''}.
      </p>
      {live && live.memberCount > 0 && (
        <p className="text-sm text-stone-700 mb-2">
          {live.answeredCount} of {live.memberCount} members have {isConfirmPhase ? 'confirmed' : 'answered'}.
        </p>
      )}
      <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 text-sm text-stone-700">
        <span>
          {isConfirmPhase && confirmedHeadcount != null
            ? (
              <>
                <span className="whitespace-nowrap">Attendees: {confirmedHeadcount} confirmed</span>
                {unconfirmedHeadcount ? <>{' · '}<span className="whitespace-nowrap">{unconfirmedHeadcount} not yet confirmed</span></> : null}
              </>
            )
            : `Attendees: ${headcount}`}
        </span>
        {!isConfirmPhase && <span>Not sure: {totals['Not sure'] || 0}</span>}
        <span>Not coming: {totals['No'] || 0}</span>
      </div>
      {live && (
        <div className="mt-2 pt-2 border-t border-stone-200">
          <p className="text-xs font-semibold text-stone-600">{isConfirmPhase ? 'Confirmed' : 'Coming'}</p>
          {confirmed.length > 0
            ? list(confirmed)
            : <p className="text-sm text-stone-400 italic mt-1">No one yet.</p>}
          {carried.length > 0 && (
            <>
              <p className="text-xs font-semibold text-stone-600 mt-2">Said yes in Phase 1, not yet confirmed</p>
              {list(carried)}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// (3 Oct 2026) Everything offered so far, every course at once (the
// contribution card shows one course at a time).
function LiveOfferingsCard({ live, myMemberId }) {
  const myAllergies = live?.myAllergies || [];
  const byCourse = live?.dishesByCourse;
  if (!byCourse || Object.keys(byCourse).length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-4 mt-4">
      <SectionLabel>What's being offered so far — not final</SectionLabel>
      {Object.entries(byCourse).map(([course, dishes]) => (
        <div key={course} className="mb-2 last:mb-0">
          <p className="text-xs font-semibold text-stone-500">{course}</p>
          {dishes && dishes.length > 0 ? (
            <ul className="space-y-0.5">
              {[...dishes].sort(byPreferredName).map((d, i) => (
                <li key={i} className="text-sm text-stone-700">
                  <span className="font-medium">{myMemberId && d.memberId === myMemberId ? 'You' : d.name}</span>
                  {/* (26 Oct 2026, decision 146) While dishes are a surprise. */}
                  {d.hidden ? <span className="text-stone-400 italic"> — a surprise</span> : <> — {d.description}</>}
                  <ContainsLine allergens={d.allergens} allergensOther={d.allergensOther} myAllergies={myAllergies} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-stone-400 italic">Nothing offered yet.</p>
          )}
        </div>
      ))}
      {live?.dishesHidden && (
        <p className="text-xs text-stone-400 mt-2">{live.dishesHiddenFromMe ? HIDDEN_DISHES_NOTE : HIDDEN_DISHES_ADMIN_NOTE}</p>
      )}
    </div>
  );
}

// Same fixed taxonomy as the backend/Settings — kept as a local copy since
// PollPage.jsx and SettingsModal.jsx are separate, independently-deployed
// components with no shared constants file.
const DIETARY_FLAG_LABELS = {
  nut: { icon: '🥜', label: 'Nut' },
  gluten: { icon: '🌾', label: 'Gluten' },
  dairy: { icon: '🥛', label: 'Dairy' },
  egg: { icon: '🥚', label: 'Egg' },
  shellfish: { icon: '🦐', label: 'Shellfish' },
  garlic: { icon: '🧄', label: 'Garlic' },
  tomato: { icon: '🍅', label: 'Tomato' },
  vegan: { icon: '🌱', label: 'Vegan' },
  vegetarian: { icon: '🥕', label: 'Vegetarian' },
  // (29 Oct 2026, decision 156) Diets.
  dairy_free: { icon: '🥛', label: 'Dairy-free' },
  gluten_free: { icon: '🌾', label: 'Gluten-free' },
  pescatarian: { icon: '🐟', label: 'Pescatarian' },
  keto: { icon: '🥑', label: 'Keto' },
  other: { icon: '📝', label: 'Other' },
};

// (26 Oct 2026, decision 147) The allergens a cook may tick on their dish: the same
// fixed list members use for their own allergies, plus "Other" in their own words.
const DISH_ALLERGENS = ['nut', 'gluten', 'dairy', 'egg', 'shellfish', 'garlic', 'tomato'];
const ALLERGEN_CAPTION = 'Optional: tick anything your dish contains, to help members with allergies.';
// (26 Oct 2026, decision 146) Neil's wording, under the offers while dishes are a surprise.
const HIDDEN_DISHES_NOTE = 'Dishes are a surprise until the day. The host will let you know if two offers clash.';
// The administrators see every dish; this reminds them that members don't yet.
const HIDDEN_DISHES_ADMIN_NOTE = "Members don't see these descriptions yet: the dishes are a surprise until the day.";

// (26 Oct 2026, decision 147) "Contains: nut, dairy" beside an offer, shown even while
// the dish itself is a surprise. Highlighted when it includes one of the viewer's own
// allergies. An untagged dish shows nothing (never "contains none").
function ContainsLine({ allergens, allergensOther, myAllergies }) {
  const list = Array.isArray(allergens) ? allergens.filter((a) => DIETARY_FLAG_LABELS[a]) : [];
  if (!list.length && !allergensOther) return null;
  const mine = new Set(Array.isArray(myAllergies) ? myAllergies : []);
  const clash = list.some((a) => mine.has(a));
  const words = [...list.map((a) => DIETARY_FLAG_LABELS[a].label.toLowerCase()), ...(allergensOther ? [allergensOther] : [])];
  return (
    <span
      className={`block text-xs mt-0.5 ${clash ? 'w-fit rounded px-1.5 py-0.5 bg-red-50 text-red-700 font-semibold border border-red-200' : 'text-stone-500'}`}
    >
      {clash ? '⚠ ' : ''}Contains: {words.join(', ')}
      {clash ? ' (one of your allergies)' : ''}
    </span>
  );
}

// (26 Oct 2026, decision 147) The cook's allergen ticks under a dish.
function DishAllergenTicks({ value, onChange }) {
  const list = value && Array.isArray(value.list) ? value.list : [];
  const other = value && typeof value.other === 'string' ? value.other : '';
  const toggle = (a) => onChange({ list: list.includes(a) ? list.filter((x) => x !== a) : [...list, a], other });
  return (
    <div className="mt-2">
      <p className="text-xs text-stone-500 mb-1.5">{ALLERGEN_CAPTION}</p>
      <div className="flex flex-wrap gap-1.5">
        {DISH_ALLERGENS.map((a) => {
          const on = list.includes(a);
          return (
            <button
              key={a}
              type="button"
              onClick={() => toggle(a)}
              aria-pressed={on}
              className={`text-xs px-2 py-1 rounded-full border whitespace-nowrap ${on ? 'bg-amber-100 border-amber-500 text-amber-900 font-medium' : 'bg-white border-stone-300 text-stone-600'}`}
            >
              {DIETARY_FLAG_LABELS[a].icon} {DIETARY_FLAG_LABELS[a].label}{on ? ' ✓' : ''}
            </button>
          );
        })}
      </div>
      <label className="mt-1.5 flex items-center gap-2">
        <span className="text-xs text-stone-600 whitespace-nowrap">📝 Other:</span>
        <input
          type="text"
          maxLength={100}
          value={other}
          onChange={(e) => onChange({ list, other: e.target.value })}
          placeholder="in your own words"
          className="flex-1 min-w-0 border border-stone-300 rounded-lg px-3 py-1.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
      </label>
    </div>
  );
}

// Small inline contact chip — clickable mailto/tel links so a dish
// contributor can reach out directly for cases a flag alone can't
// capture (e.g. "vegetarian but eats duck"). Phone is optional per
// member, so its link only renders when supplied.
// (23 Oct 2026, decision 124) The contact links only: since decision 124 the name sits
// inside the chip's brackets and these icons follow after the closing bracket.
function MemberContactIcons({ member }) {
  return (
    <React.Fragment>
      {member.email && (
        <a href={`mailto:${member.email}`} title={member.email} className="text-current">
          <Mail className="w-3 h-3 inline" />
        </a>
      )}
      {member.phone && (
        <a href={`tel:${member.phone}`} title={member.phone} className="text-current">
          <Phone className="w-3 h-3 inline" />
        </a>
      )}
    </React.Fragment>
  );
}

// (21 Oct 2026, 8.42, decision 119) Each chip reads "{item} ({Preferred Name})", e.g.
// "Vegetarian (Gary)". An "Other" flag shows the member's own words in place of "Other":
// "Alcohol & chocolate, Josie (Allan & Josie)".
// (23 Oct 2026, decision 124, amending 119) One chip per member, also when several members
// share an item ("Vegetarian (Gary)", "Vegetarian (Tim & Teri)"), so every icon clearly
// belongs to its name. The bracket holds the Preferred Name and, where an administrator
// added the flag, ", added by {name}"; the email and phone icons follow after the closing
// bracket: "Gluten (Turea & Allan) ✉". Kept the same in PollPage.jsx and PollResultsPage.jsx.
function DietaryChips({ flags, chipClass, addedClass }) {
  const chips = [];
  (flags || []).forEach((f) => {
    const icon = DIETARY_FLAG_LABELS[f.flag_type]?.icon || '📝';
    const label = DIETARY_FLAG_LABELS[f.flag_type]?.label || f.flag_type;
    (f.members || []).forEach((m, i) => {
      const text = f.flag_type === 'other' ? (String(m.other_text || '').trim() || label) : label;
      chips.push(
        <span key={`${f.flag_type}-${i}`} className={`inline-flex items-center gap-1 ${chipClass}`}>
          <span>
            {icon}{' '}{text}{' ('}{m.name}
            {m.enteredByName && (
              <span className={`italic ${addedClass}`}>, added by {m.enteredByName}</span>
            )}
            {')'}
          </span>
          <MemberContactIcons member={m} />
        </span>
      );
    });
  });
  return <>{chips}</>;
}

// Confirm-phase-only memory-jogger, per the signed-off design: never
// asserts a dish is safe, always frames itself as a reminder. Visible
// regardless of which course tab is active — attendee flags don't
// change based on what the contributor is currently looking at.
function DietaryFlagsBanner({ dietaryFlags }) {
  const [expanded, setExpanded] = useState(true);
  if (!dietaryFlags || dietaryFlags.length === 0) return null;

  const allergies = dietaryFlags.filter((f) => f.severity === 'allergy');
  // (29 Oct 2026, decision 156) Diets, between Allergies and Aversions.
  const diets = dietaryFlags.filter((f) => f.severity === 'diet');
  const aversions = dietaryFlags.filter((f) => f.severity === 'aversion');

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 mb-3 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-3.5 py-2.5"
      >
        {/* (24 Sep 2026) Same size/weight as the "Your contribution" heading: the
            panel now sits above the course tabs, as a heading in its own right. */}
        <span className="text-sm font-medium text-amber-900">
          Attending members have dietary needs
        </span>
        <span className="text-amber-700 text-xs">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-3.5 pb-3.5">
          {allergies.length > 0 && (
            <div className="mb-2.5">
              <p className="text-[11px] font-semibold text-rose-900 tracking-tight mb-1.5">
                Allergies
              </p>
              <div className="flex flex-wrap gap-1.5">
                <DietaryChips
                  flags={allergies}
                  chipClass="text-[11px] bg-white border border-rose-200 text-rose-900 rounded-full px-2 py-1"
                  addedClass="text-rose-700"
                />
              </div>
            </div>
          )}

          {diets.length > 0 && (
            <div className="mb-2.5">
              <p className="text-[11px] font-semibold text-sky-900 tracking-tight mb-1.5">
                Diets
              </p>
              <div className="flex flex-wrap gap-1.5">
                <DietaryChips
                  flags={diets}
                  chipClass="text-[11px] bg-white border border-sky-200 text-sky-900 rounded-full px-2 py-1"
                  addedClass="text-sky-700"
                />
              </div>
            </div>
          )}

          {aversions.length > 0 && (
            <div className="mb-3">
              <p className="text-[11px] font-semibold text-green-800 tracking-tight mb-1.5">
                Aversions
              </p>
              <div className="flex flex-wrap gap-1.5">
                <DietaryChips
                  flags={aversions}
                  chipClass="text-[11px] bg-white border border-green-200 text-green-800 rounded-full px-2 py-1"
                  addedClass="text-green-700"
                />
              </div>
            </div>
          )}

          <p className="text-[11px] text-amber-800 leading-relaxed border-t border-amber-200 pt-2.5">
            A reminder, not a guarantee — check ingredients with attendees directly if you're unsure.
          </p>
        </div>
      )}
    </div>
  );
}

// (9 Sep 2026) Copied verbatim from DashboardStep.jsx's toast markup — same
// visual treatment, same z-index, same centered/pointer-events pattern —
// so a status message looks identical whether it fires from the dashboard
// or from here.
function StatusToast({ statusMessage }) {
  // (26 Sep 2026) Now the shared component (StatusToast.jsx), so messages
  // here look and behave exactly as everywhere else.
  return <SharedStatusToast statusMessage={statusMessage} />;
}

// (9 Sep 2026) Proxy mode indicator — shown whenever a GA is answering on
// behalf of a member, in both the no-poll/error state and the normal
// render, so it's never ambiguous whose answer is being edited.
function ProxyBanner({ name, isLateEntry }) {
  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 px-3.5 py-2.5 mb-3">
      <p className="text-xs font-medium text-blue-900">
        Answering on behalf of {name || 'this member'}
      </p>
      {/* (14 Sep 2026) Late-entry override — this poll's phase has
          already closed; without this, a GA could mistake this for a
          still-live poll they're editing normally. */}
      {isLateEntry && (
        <p className="text-xs font-medium text-amber-700 mt-1">
          This poll is closed — this is a late entry, not a live poll.
        </p>
      )}
    </div>
  );
}

// (25 Oct 2026, decision 136) How far the on-screen keyboard (or any browser bar)
// covers the bottom of the window, so the unsaved-changes bar can sit just above it.
// iPhone Safari keeps a fixed bottom bar behind the keyboard otherwise. 0 where the
// browser has no visualViewport.
function useBottomInset() {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;
    const update = () => {
      // Zoomed in (pinch): the measurement no longer matches the page's own pixels,
      // so the bar simply sits at the bottom of the window.
      if (vv.scale && vv.scale > 1.01) { setInset(0); return; }
      const covered = window.innerHeight - (vv.height + vv.offsetTop);
      setInset(covered > 1 ? Math.round(covered) : 0);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);
  return inset;
}

const DEFAULT_OFFER_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'Not sure', label: 'Not sure yet' },
  { value: 'No', label: 'No' },
];

const DEFAULT_CONFIRM_OPTIONS = [
  { value: 'Yes', label: 'Yes, coming' },
  { value: 'No', label: "No, can't make it" },
];

// (25 Sep 2026) Unsaved-changes check. A normalised picture of the answers
// on the page — what Save would send — compared with the same picture as of
// the last load or save. Party size only counts when attending is Yes (the
// stepper is hidden otherwise); text is trimmed so stray spaces don't count;
// the cooking requirement only counts alongside a dish, since Save clears
// it when there's none.
// (26 Oct 2026, decision 147) A dish's allergen ticks count too, alongside its dish.
function tidyTicks(t) {
  return {
    list: [...((t && Array.isArray(t.list)) ? t.list : [])].sort(),
    other: ((t && t.other) || '').trim(),
  };
}
function answersSnapshot(attendance, partySize, offerings, dishQs, cookingQ, ticks) {
  const dishes = {};
  let anyDish = false;
  dishQs.forEach((q) => {
    const v = (offerings[q.id] || '').trim();
    dishes[q.id] = v ? { v, t: tidyTicks(ticks && ticks[q.id]) } : '';
    if (v) anyDish = true;
  });
  const cooking = cookingQ && anyDish ? (offerings[cookingQ.id] || '').trim() : '';
  return JSON.stringify({
    attendance: attendance || null,
    partySize: attendance === 'Yes' ? Math.max(1, partySize || 1) : null,
    dishes,
    cooking,
  });
}

// (26 Sep 2026) Course tabs always in eating order — Starter, Main,
// Dessert, then Other (and any "Other (…)" variant) — whatever order the
// poll's questions arrive in. Polls created before 24 Sep follow the order
// the offers were made, which put Dessert first. Unknown course names keep
// their original order, after these.
const COURSE_RANK = { starter: 0, main: 1, dessert: 2 };
const courseRank = (course) => {
  const c = String(course || '').trim().toLowerCase();
  if (c in COURSE_RANK) return COURSE_RANK[c];
  if (c.startsWith('other')) return 3;
  return 4;
};
const sortByCourse = (qs) => qs
  .map((q, i) => ({ q, i }))
  .sort((a, b) => (courseRank(a.q.config?.course) - courseRank(b.q.config?.course)) || (a.i - b.i))
  .map((x) => x.q);

export default function PollPage({
  eventUid,
  apiBaseUrl,
  authToken,
  authenticatedFetch,
  formatShortDate,
  formatEventDate,
  formatTime,
  getTzAbbr,
  userLocalTimezone,
  onBack,
  onStatusMessage,
  defaultPartySize = 2,
  // (8 Sep 2026) Added for the shared PollHeaderBar — same pattern as
  // MemberCalendarStep.jsx's own header/SettingsModal usage.
  userEmail,
  preferredName,
  firstName,
  lastName,
  isAuthenticated,
  onSignOut,
  showSettings,
  setShowSettings,
  settingsInitialTab,
  setSettingsInitialTab,
  onSaveProfile,
  userTier,
  currentMemberCalendar,
  // (9 Sep 2026) Proxy mode — Pass 4 of proxy-entry (design agreed 8 Sep).
  // When proxyMode is true, every fetch/submit call below is redirected to
  // the GA-proxy poll endpoints for proxyMemberId on proxyCalendarId
  // instead of the caller's own self-service endpoints, and an "Answering
  // on behalf of" banner is shown. onExitProxyMode is called by onBack
  // when set, so the caller (whatever screen launched proxy mode) decides
  // where "back" returns to.
  proxyMode = false,
  proxyCalendarId,
  proxyMemberId,
  // (14 Sep 2026) Late-entry override — when set, fetches this exact
  // poll (regardless of status) instead of re-resolving "whatever's
  // open" for eventUid, which a closed poll wouldn't match. Only
  // meaningful together with proxyMode; undefined for the normal case.
  proxyPollId,
  // (9 Sep 2026) Fixing a pre-existing gap found during proxy testing:
  // onStatusMessage updates App.js's shared statusMessage state
  // correctly, but nothing in this component ever rendered it back —
  // DashboardStep.jsx has its own copy of the toast markup, but PollPage
  // never got one, so every status message (success or error) has been
  // silently invisible here the whole time this page has existed. Not
  // something Pass 4 introduced, but it's what made Step 5's 403 look
  // like a silent failure instead of a clear error.
  statusMessage,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(null);   // { poll, event, closesOn }
  // (2 Oct 2026) Not in proxy mode: there the administrator is answering for someone else.
  const secondaryOf = useSecondaryOf(apiBaseUrl, authToken, proxyMode ? null : current?.event?.calendarId);
  const [pollState, setPollState] = useState(null); // { poll, questions, myResponses, attendanceTotals, alreadyOffered }
  // (3 Oct 2026) Live results while the poll is open: who's coming by name,
  // how many have answered, and what's being offered — from the same
  // /polls/results route the results page uses. Not final; labelled so.
  const [live, setLive] = useState(null);
  const [saving, setSaving] = useState(false);
  // (9 Sep 2026) Proxy mode — target member's display name, taken from
  // the proxy endpoints' targetMember field rather than passed in as a
  // prop, so it's always accurate even if the caller only had an id handy.
  const [proxyTargetName, setProxyTargetName] = useState(null);
  // (23 Sep 2026) "is" or "are" for the proxy heading: a couple's preferred
  // name ("Max & Phyllis", "Pete and Mary") takes "are", one person "is".
  const proxyVerb = /\s(&|and)\s/i.test(proxyTargetName || '') ? 'are' : 'is';

  // Form state
  const [attendance, setAttendance] = useState(null);
  const [partySize, setPartySize] = useState(Math.max(1, defaultPartySize || 1));
  const [activeCourse, setActiveCourse] = useState(null);
  const [offerings, setOfferings] = useState({}); // { [questionId]: text }
  const [allergenTicks, setAllergenTicks] = useState({}); // (decision 147) { [questionId]: { list, other } }
  // (11 Sep 2026) Tracks which questionIds had a genuine prior answer as of
  // the last load/save — lets handleSave tell "never touched this course"
  // (skip — leave any other member's row alone) apart from "explicitly
  // cleared a course I'd previously offered" (send an explicit withdrawal),
  // since both look identical as an empty string in `offerings`.
  const hadAnswerRef = useRef(new Set());
  // (25 Sep 2026) Unsaved changes: snapshot of the answers as last loaded or
  // saved (null until the poll has loaded), the "leave this page?" dialog's
  // pending exit, and the attendance nudge shown when Save is tapped before
  // attendance is answered.
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [leavePrompt, setLeavePrompt] = useState(null); // { action } | null
  const [attendanceNudge, setAttendanceNudge] = useState(false);
  const [nudgeFlash, setNudgeFlash] = useState(false);
  const attendanceCardRef = useRef(null);
  const bottomInset = useBottomInset(); // (25 Oct 2026, decision 136)
  // (25 Oct 2026, decision 136) Whether the Save button at the end of the page is on
  // screen: the bar is not needed then, so the two never show at once.
  const saveButtonRef = useRef(null);
  const [saveButtonVisible, setSaveButtonVisible] = useState(false);
  useEffect(() => {
    const el = saveButtonRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver(([entry]) => setSaveButtonVisible(entry.isIntersecting), { threshold: 0.6 });
    obs.observe(el);
    return () => obs.disconnect();
  });

  const status = (text, type = 'info') => {
    if (onStatusMessage) onStatusMessage(text, type);
  };

  // (9 Sep 2026) Proxy mode — same three endpoints, nested under
  // /calendars/:calendarId/members/:memberId/... instead of the
  // self-service paths. Built once here rather than inline at each call
  // site so the two fetches below and handleSave's POST all stay in sync.
  const currentPollUrl = proxyMode
    ? `${apiBaseUrl}/api/calendars/${proxyCalendarId}/members/${proxyMemberId}/events/${eventUid}/polls/current${proxyPollId ? `?pollId=${proxyPollId}` : ''}`
    : `${apiBaseUrl}/api/events/${eventUid}/polls/current`;
  const pollStateUrl = (pollId) => proxyMode
    ? `${apiBaseUrl}/api/calendars/${proxyCalendarId}/members/${proxyMemberId}/polls/${pollId}/state`
    : `${apiBaseUrl}/api/polls/${pollId}/state`;
  const bulkResponsesUrl = (pollId) => proxyMode
    ? `${apiBaseUrl}/api/calendars/${proxyCalendarId}/members/${proxyMemberId}/polls/${pollId}/responses/bulk`
    : `${apiBaseUrl}/api/polls/${pollId}/responses/bulk`;

  const loadLive = useCallback(async () => {
    try {
      const res = await authenticatedFetch(`${apiBaseUrl}/api/events/${eventUid}/polls/results`);
      if (res.ok) setLive(await res.json());
    } catch (err) {
      // Live results are extra: the poll itself still works without them.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventUid, apiBaseUrl]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const curRes = await authenticatedFetch(currentPollUrl);
      if (!curRes.ok) {
        const body = await curRes.json().catch(() => ({}));
        throw new Error(body.error || `Could not load this event (HTTP ${curRes.status}).`);
      }
      const curData = await curRes.json();
      setCurrent(curData);
      if (proxyMode && curData.targetMember) setProxyTargetName(curData.targetMember.name);

      if (!curData.poll) {
        setPollState(null);
        setLoading(false);
        return;
      }

      const stateRes = await authenticatedFetch(pollStateUrl(curData.poll.id));
      if (!stateRes.ok) {
        const body = await stateRes.json().catch(() => ({}));
        throw new Error(body.error || 'Could not load the poll.');
      }
      const stateData = await stateRes.json();
      setPollState(stateData);
      if (proxyMode && stateData.targetMember) setProxyTargetName(stateData.targetMember.name);

      // Initialize form state from this member's existing answers.
      const attendingQ = stateData.questions.find((q) => q.role === 'attending');
      const dishQs = stateData.questions.filter((q) => q.role === 'dish_outline');

      let loadedAttendance = null;
      let loadedPartySize = Math.max(1, defaultPartySize || 1);
      if (attendingQ) {
        const mine = stateData.myResponses[attendingQ.id];
        loadedAttendance = mine ? mine.value : null;
        loadedPartySize = mine && mine.follow_up_value
          ? Math.max(1, Number(mine.follow_up_value) || 1)
          : Math.max(1, defaultPartySize || 1);
        setAttendance(loadedAttendance);
        setPartySize(loadedPartySize);
      }

      const initialOfferings = {};
      const initialTicks = {};
      const hadAnswerSet = new Set();
      dishQs.forEach((q) => {
        const mine = stateData.myResponses[q.id];
        initialOfferings[q.id] = mine ? mine.value : '';
        initialTicks[q.id] = {
          list: mine && Array.isArray(mine.allergens) ? mine.allergens : [],
          other: mine && mine.allergens_other ? mine.allergens_other : '',
        };
        if (mine) hadAnswerSet.add(q.id);
      });
      const cookingReqQ = stateData.questions.find((q) => q.role === 'cooking_requirements');
      if (cookingReqQ) {
        const mine = stateData.myResponses[cookingReqQ.id];
        initialOfferings[cookingReqQ.id] = mine ? mine.value : '';
        if (mine) hadAnswerSet.add(cookingReqQ.id);
      }
      hadAnswerRef.current = hadAnswerSet;
      setOfferings(initialOfferings);
      setAllergenTicks(initialTicks);
      setSavedSnapshot(answersSnapshot(
        attendingQ ? loadedAttendance : null,
        loadedPartySize,
        initialOfferings,
        dishQs,
        cookingReqQ,
        initialTicks
      ));
      if (dishQs.length && !activeCourse) {
        setActiveCourse(sortByCourse(dishQs)[0].config.course);
      }
    } catch (err) {
      console.error('Error loading poll:', err);
      setError(err.message || 'Something went wrong loading this poll.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventUid, apiBaseUrl, proxyMode, proxyCalendarId, proxyMemberId, proxyPollId]);

  useEffect(() => {
    loadAll();
    loadLive();
  }, [loadAll, loadLive]);

  const questions = useMemo(() => pollState?.questions || [], [pollState]);
  const attendingQuestion = useMemo(() => questions.find((q) => q.role === 'attending'), [questions]);
  const dishQuestions = useMemo(() => questions.filter((q) => q.role === 'dish_outline'), [questions]);
  // (24 Sep 2026) One tab per course, in the poll's question order (the
  // template's eating order: Starter, Main, Dessert, Other). A confirm poll
  // created from today has exactly one dish question per course, so this
  // is just dishQuestions. Older confirm polls have one question per Phase 1
  // OFFER instead (possibly several per course, and none for a course nobody
  // offered): for those, each course appears once, using the question this
  // member has an answer on, else the first one for that course.
  const tabQuestions = useMemo(() => {
    const mine = pollState?.myResponses || {};
    const byCourse = new Map();
    for (const q of dishQuestions) {
      const course = q.config.course;
      const current = byCourse.get(course);
      if (!current || (!mine[current.id] && mine[q.id])) byCourse.set(course, q);
    }
    return sortByCourse([...byCourse.values()]);
  }, [dishQuestions, pollState]);
  // (24 Sep 2026) A cooking requirement only means something alongside a
  // dish: the box shows only once this member has a dish entered, and
  // clearing their last dish clears the requirement in the same save.
  const hasAnyDish = tabQuestions.some((q) => offerings[q.id] && offerings[q.id].trim());
  // Confirm-phase only (one shared question per poll, created in
  // closeOfferPollAndAdvance) — an optional request TO the host, e.g.
  // "air fryer", "grill for 10 min". Fulfillment/availability is handled
  // by the host out-of-band (WhatsApp), not tracked in-app.
  const cookingReqQuestion = useMemo(
    () => questions.find((q) => q.role === 'cooking_requirements'),
    [questions]
  );

  const attendanceOptions = useMemo(() => {
    const fallback = current?.poll?.phase === 'confirm' ? DEFAULT_CONFIRM_OPTIONS : DEFAULT_OFFER_OPTIONS;
    const raw = attendingQuestion?.config?.options;
    if (!Array.isArray(raw) || raw.length === 0) return fallback;
    // (17 Aug 2026) Backend stores config.options as plain strings — e.g.
    // closeOfferPollAndAdvance writes options: ['Yes', 'No'] for the
    // confirm-phase question, not {value,label} objects. Normalize here:
    // match each raw string against our known defaults for a friendlier
    // label ("Yes, coming" rather than bare "Yes"), falling back to the
    // raw string itself for anything unrecognized. Also passes through
    // objects unchanged, in case a future template does supply them.
    const allKnown = [...DEFAULT_OFFER_OPTIONS, ...DEFAULT_CONFIRM_OPTIONS];
    return raw.map((opt) => {
      if (typeof opt === 'string') {
        return allKnown.find((k) => k.value === opt) || { value: opt, label: opt };
      }
      return opt;
    });
  }, [attendingQuestion, current]);

  const isConfirmPhase = current?.poll?.phase === 'confirm';

  const setDish = (questionId, value) => {
    setOfferings((prev) => ({ ...prev, [questionId]: value }));
  };

  const myOfferingCount = Object.values(offerings).filter((v) => v && v.trim()).length;

  // (13 Sep 2026) Save/Confirm is gated on the answer being complete (an
  // attending question always exists — singleton per poll — and must be
  // explicitly answered) rather than on whether anything changed since
  // load. The prior "only enable if something differs from what loaded"
  // check let a member reach Save with no attendance answer at all
  // (attendance stayed null, dish edits alone made the button dirty), so
  // no attendance response was ever sent to the backend — and it also
  // blocked a member from explicitly reconfirming an answer that was
  // already correct as loaded (same shape as the Poll Settings save-button
  // bug this session). Requiring attendance to be answered fixes the first
  // problem and, by no longer keying off "changed", the second as well.
  const needsAttendance = !!attendingQuestion && attendance === null;

  // (25 Sep 2026) Wording for the attendance prompts, by phase and by whose
  // answer this is.
  const attendanceChoices = isConfirmPhase ? 'Yes or No' : 'Yes, Not sure yet or No';
  const whoseAnswer = proxyMode ? `${proxyTargetName || 'This member'}'s answer` : 'Your answer';

  // (25 Sep 2026) Anything on the page that differs from the last load/save.
  const isDirty = savedSnapshot !== null && !!pollState &&
    answersSnapshot(
      attendingQuestion ? attendance : null,
      partySize,
      offerings,
      dishQuestions,
      cookingReqQuestion,
      allergenTicks
    ) !== savedSnapshot;

  // (25 Sep 2026) Save used to be disabled until attendance was answered,
  // and a disabled button gives no response when tapped: easy to miss at the
  // bottom of a long page, so a member (or GA in proxy mode) could tap it,
  // leave, and lose their dish. Now Save stays enabled; tapping it without
  // an attendance answer scrolls up to the attendance question, highlights
  // it and says why nothing was saved.
  const nudgeAttendance = () => {
    setAttendanceNudge(true);
    setNudgeFlash(true);
    setTimeout(() => setNudgeFlash(false), 1600);
    if (attendanceCardRef.current) {
      attendanceCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // (25 Sep 2026) Every way off this page (back arrow — which is also Back
  // to results in proxy mode — and Sign Out) goes through here: with
  // unsaved changes, ask first.
  const guardedLeave = (action) => {
    if (!action) return;
    if (isDirty) setLeavePrompt({ action });
    else action();
  };

  // (25 Sep 2026) Closing or refreshing the tab: the browser's own warning
  // (its wording can't be changed, and iPhone Safari may not show it).
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  // (25 Sep 2026) Returns true only when the answer was actually saved, so
  // the leave dialog's "Save and leave" knows whether it's safe to go.
  const handleSave = async () => {
    if (!current?.poll) return false;

    // (13 Sep 2026) Attendance must be answered before anything is sent, so
    // a real attendance response always exists for this poll.
    // (25 Sep 2026) Now the only guard (the button is no longer disabled):
    // show the member where to answer instead of a toast they may not see.
    if (attendingQuestion && !attendance) {
      nudgeAttendance();
      return false;
    }

    const answers = [];

    if (attendingQuestion && attendance) {
      answers.push({
        questionId: attendingQuestion.id,
        answer:
          attendance === 'Yes'
            // (13 Sep 2026) Clamp at submit time too — the Stepper's own
            // floor only guards manual clicks, not a loaded/default value
            // that arrived as 0 or lower.
            ? { value: attendance, follow_up_value: Math.max(1, partySize || 1) }
            : { value: attendance },
      });
    }

    dishQuestions.forEach((q) => {
      const val = offerings[q.id];
      if (val && val.trim()) {
        // (26 Oct 2026, decision 147) With the cook's allergen ticks, if any.
        const t = tidyTicks(allergenTicks[q.id]);
        const answer = { value: val.trim() };
        if (t.list.length) answer.allergens = t.list;
        if (t.other) answer.allergens_other = t.other;
        answers.push({ questionId: q.id, answer });
      } else if (hadAnswerRef.current.has(q.id)) {
        // (11 Sep 2026) Explicitly cleared a course that previously had an
        // offer — withdraw it, rather than silently leaving the old answer
        // in place (the bug: blanking the text and saving a different
        // course left the cleared course's prior answer untouched, since
        // nothing was ever sent for it).
        answers.push({ questionId: q.id, answer: null });
      }
    });

    if (cookingReqQuestion) {
      const val = offerings[cookingReqQuestion.id];
      // Only send if the member actually typed something, OR they're
      // explicitly clearing a previously-saved answer — never upsert an
      // empty string over a prior answer, and never silently leave a
      // stale one in place either.
      if (hasAnyDish && val && val.trim()) {
        answers.push({ questionId: cookingReqQuestion.id, answer: { value: val.trim() } });
      } else if (hadAnswerRef.current.has(cookingReqQuestion.id)) {
        answers.push({ questionId: cookingReqQuestion.id, answer: null });
      }
    }

    if (answers.length === 0) {
      status('Nothing to save yet.', 'info');
      return false;
    }

    setSaving(true);
    try {
      const res = await authenticatedFetch(bulkResponsesUrl(current.poll.id), {
        method: 'POST',
        body: JSON.stringify({ answers }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Could not save your answer.');
      }
      const savedMessage = proxyMode
        ? `Answer saved for ${proxyTargetName || 'this member'}.`
        : (isConfirmPhase ? 'Final answer confirmed.' : 'Your answer has been saved.');
      status(savedMessage, 'success');
      // (13 Sep 2026) No dirty-baseline reset needed anymore — Save/Confirm
      // is gated on attendance being answered, not on a snapshot diff.
      // Bring hadAnswerRef in line with what was just saved, so a second
      // clear-then-save cycle behaves correctly without a full reload.
      const newHadAnswer = new Set(hadAnswerRef.current);
      dishQuestions.forEach((q) => {
        const val = offerings[q.id];
        if (val && val.trim()) newHadAnswer.add(q.id); else newHadAnswer.delete(q.id);
      });
      if (cookingReqQuestion) {
        const val = offerings[cookingReqQuestion.id];
        if (hasAnyDish && val && val.trim()) newHadAnswer.add(cookingReqQuestion.id); else newHadAnswer.delete(cookingReqQuestion.id);
        if (!hasAnyDish) setDish(cookingReqQuestion.id, '');
      }
      hadAnswerRef.current = newHadAnswer;
      // (25 Sep 2026) What's on the page is now what's saved.
      setSavedSnapshot(answersSnapshot(
        attendingQuestion ? attendance : null,
        partySize,
        offerings,
        dishQuestions,
        cookingReqQuestion,
        allergenTicks
      ));
      setAttendanceNudge(false);
      // Refresh live totals / already-offered list to reflect the save.
      const stateRes = await authenticatedFetch(pollStateUrl(current.poll.id));
      if (stateRes.ok) setPollState(await stateRes.json());
      loadLive();
      return true;
    } catch (err) {
      console.error('Error saving poll response:', err);
      status(err.message || 'Could not save your answer.', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // (25 Sep 2026) The leave dialog's three outcomes.
  const leaveSaveAndGo = async () => {
    const pending = leavePrompt;
    const ok = await handleSave();
    if (ok) {
      setLeavePrompt(null);
      if (pending) pending.action();
    }
  };
  const leaveWithoutSaving = () => {
    const pending = leavePrompt;
    setLeavePrompt(null);
    if (pending) pending.action();
  };
  const leaveStay = () => {
    setLeavePrompt(null);
    if (needsAttendance) nudgeAttendance();
  };

  // ---------------------------------------------------------------------
  // Loading / error / no-poll states
  // ---------------------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center" style={{ minHeight: '100dvh' }}>
        <StatusToast statusMessage={statusMessage} />
        <p className="text-sm text-stone-500">Loading poll…</p>
      </div>
    );
  }

  // (8 Sep 2026) Shared with the normal-render subtitle below — one line
  // combining date/time/location, matching PollResultsPage.jsx's pattern.
  // (13 Sep 2026) Time zone abbreviation now folded in here, right next to
  // the time it actually describes — same inline pattern as DashboardStep's
  // event-card time line — rather than a separate paragraph lower on the
  // page with no visible time near it.
  const formatEventSubtitle = (evt) => {
    if (!evt) return undefined;
    const showTz = evt.timezone && evt.timezone !== userLocalTimezone;
    const tzAbbr = showTz
      ? (getTzAbbr ? (getTzAbbr(evt.timezone, evt.eventDate) || evt.timezone) : evt.timezone)
      : null;
    return (
      `${formatEventDate ? formatEventDate(evt.eventDate) : evt.eventDate}` +
      (evt.startTime
        ? ` · ${formatTime ? formatTime(evt.startTime) : evt.startTime}${tzAbbr ? ' ' + tzAbbr : ''}`
        : '') +
      (evt.location ? ` · ${evt.location}` : '')
    );
  };

  if (error || !current?.poll) {
    return (
      <div className="min-h-screen bg-stone-100" style={{ minHeight: '100dvh' }}>
        <StatusToast statusMessage={statusMessage} />
        <PollHeaderBar
          title={current?.event?.title || 'Poll'}
          subtitle={formatEventSubtitle(current?.event)}
          userEmail={userEmail}
          preferredName={preferredName}
          firstName={firstName}
          lastName={lastName}
          isAuthenticated={isAuthenticated}
          onSignOut={onSignOut}
          onOpenSettings={() => setShowSettings(true)}
          onBack={onBack}
          secondaryOf={secondaryOf}
        />
        <div className="max-w-sm mx-auto px-4 py-6">
          {proxyMode && <ProxyBanner name={proxyTargetName} isLateEntry={current?.poll?.status === 'closed'} />}
          {current?.event?.description && (
            <p className="text-xs text-stone-500 mb-4 border-b border-stone-200 pb-3">{current.event.description}</p>
          )}
          <div className="bg-white rounded-xl border border-stone-200 p-4 text-center">
            <p className="text-sm text-stone-500">
              {error || "There's no open poll for this event right now."}
            </p>
          </div>
        </div>
        {showSettings && (
          <SettingsModal
            isOpen={showSettings}
            onClose={() => { setShowSettings(false); setSettingsInitialTab('profile'); }}
            userEmail={userEmail}
            apiBaseUrl={apiBaseUrl}
            authToken={authToken}
            onStatusMessage={onStatusMessage}
            userTier={userTier}
            initialTab={settingsInitialTab}
            currentCalendar={currentMemberCalendar}
            firstName={firstName}
            lastName={lastName}
            onSaveProfile={onSaveProfile}
            restrictToProfileTabs={true}
          />
        )}
      </div>
    );
  }

  const { event, closesOn } = current;
  const totals = pollState?.attendanceTotals?.counts || {};
  const alreadyOffered = pollState?.alreadyOffered || null;

  return (
    <div className="min-h-screen bg-stone-100" style={{ minHeight: '100dvh' }}>
      <StatusToast statusMessage={statusMessage} />
      <PollHeaderBar
        title={event.title}
        subtitle={formatEventSubtitle(event)}
        userEmail={userEmail}
        preferredName={preferredName}
        firstName={firstName}
        lastName={lastName}
        isAuthenticated={isAuthenticated}
        onSignOut={() => guardedLeave(onSignOut)}
        onOpenSettings={() => setShowSettings(true)}
        onBack={onBack ? () => guardedLeave(onBack) : undefined}
        secondaryOf={secondaryOf}
      />

      {/* (25 Oct 2026, decision 136) Extra room at the bottom while the unsaved-changes bar
          shows, so it never covers the end of the page. */}
      <div className={`max-w-sm mx-auto px-4 py-6 ${isDirty ? 'pb-28' : 'pb-8'}`}>
        {proxyMode && <ProxyBanner name={proxyTargetName} isLateEntry={current?.poll?.status === 'closed'} />}
        {event.description && (
          <p className="text-xs text-stone-500 mb-4 border-b border-stone-200 pb-3">{event.description}</p>
        )}

        {/* Attendance */}
        {attendingQuestion && (
          <div
            ref={attendanceCardRef}
            className={`bg-white rounded-xl border p-4 mb-4 transition-shadow ${
              attendanceNudge && needsAttendance
                ? `border-rose-400 ring-2 ring-rose-400 ${nudgeFlash ? 'animate-pulse' : ''}`
                : 'border-stone-200'
            }`}
          >
            <SectionLabel>
              {isConfirmPhase
                ? 'Phase 2: final confirmation of attendance'
                : (proxyMode ? `Phase 1: ${proxyVerb} ${proxyTargetName || 'this member'} coming?` : 'Phase 1: are you coming?')}
            </SectionLabel>
            <MyAnswerLine
              saved={attendingQuestion ? (pollState?.myResponses || {})[attendingQuestion.id] : null}
              hasRespondedThisPhase={!!pollState?.myLastRespondedAt}
              isConfirmPhase={isConfirmPhase}
              who={proxyMode ? (proxyTargetName || 'This member') : null}
              verb={proxyVerb}
            />
            <div className={`grid gap-2 ${attendanceOptions.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {attendanceOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setAttendance(opt.value); setAttendanceNudge(false); }}
                  className={`rounded-lg py-2.5 text-sm font-medium border transition-colors ${
                    attendance === opt.value
                      ? 'bg-green-700 border-green-700 text-white'
                      : 'bg-white border-stone-300 text-stone-700 active:bg-stone-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {attendanceNudge && needsAttendance && (
              <p className="text-sm font-medium text-rose-700 mt-3" role="alert">
                Choose {attendanceChoices} first — {proxyMode ? whoseAnswer : 'your answer'} hasn't been saved yet.
              </p>
            )}

            {closesOn && (
              <p className="text-xs text-stone-500 mt-3 mb-3">
                {/* (25 Sep 2026) Proxy mode: the member's answer, not the GA's. */}
                {/* (21 Oct 2026, 8.42, decisions 105, 113) The closing time and the date in full. */}
                {proxyMode
                  ? `${proxyTargetName || 'This member'}'s answer can be changed any time before the poll closes at 7 a.m. on `
                  : 'You can change your answer any time before the poll closes at 7 a.m. on '}
                {fullDayDate(closesOn) || closesOn}.
              </p>
            )}

            {attendance === 'Yes' && (
              <div className="rounded-lg bg-stone-50 border border-stone-200 p-3 mb-3">
                <p className="text-sm font-medium text-stone-700">
                  {proxyMode ? `How many, including ${proxyTargetName || 'them'}?` : 'How many, including you?'}
                </p>
                <p className="text-xs text-stone-500 mt-0.5 mb-3">
                  {proxyMode
                    ? 'Their household — partner, guests, family coming with them.'
                    : 'Speak for your household — partner, guests, family coming with you.'}
                </p>
                <Stepper value={partySize} onChange={setPartySize} />
              </div>
            )}

            {pollState?.attendanceTotals && (
              <LiveResultsBox
                totals={totals}
                headcount={pollState.attendanceTotals.headcount || 0}
                confirmedHeadcount={pollState.attendanceTotals.confirmedHeadcount}
                unconfirmedHeadcount={pollState.attendanceTotals.unconfirmedHeadcount || 0}
                live={live}
                isConfirmPhase={isConfirmPhase}
                closesOnText={closesOn ? (fullDayDate(closesOn) || closesOn) : null}
                myMemberId={proxyMode ? null : pollState?.memberId}
              />
            )}
          </div>
        )}

        {/* Course contribution */}
        {dishQuestions.length > 0 && (
          <div className="bg-white rounded-xl border border-stone-200 p-4">
            <p className="text-sm font-medium text-stone-700 mb-1">
              {isConfirmPhase
                ? (proxyMode ? (proxyTargetName ? `${proxyTargetName}'s contribution` : 'Their contribution') : 'Your contribution')
                : (proxyMode
                    ? `What course and dish will ${proxyTargetName || 'this member'} provide?`
                    : 'What course and dish will you provide?')}
            </p>
            {!isConfirmPhase && !proxyMode && (
              <p className="text-xs text-stone-500 mb-3">
                Bringing more than one thing? Fill in as many courses as you like. If you're coming as
                a couple, one of you only needs to enter your joint contribution.
              </p>
            )}

            {/* (21 Sep 2026) On a phone the tabs share the row's width and a long caption (e.g. "Other
                (e.g. nibbles)") wraps INSIDE its tab instead of running off the edge; items-stretch
                makes every tab as tall as the tallest one. From sm up the tabs keep their natural
                width, as before.
                (23 Sep 2026) overflow-x-auto replaced by flex-wrap: the sideways-scrolling row showed a
                scroll-bar line under the tabs on an iPhone for a few seconds after every scroll, and on a
                narrow screen could cut the last tab off ("Other (e.g. nibbl..."), hiding a course. Tabs now
                wrap onto a second line instead, so every course is always visible.
                (23 Sep 2026, same day) On a phone the tabs now sit in a 2 x 2 grid (Starter | Main,
                Dessert | Other) rather than wrapping, which left "Other (e.g. nibbles)" stretched across a
                row of its own. From sm up the row wraps at natural width, as before.
                (25 Sep 2026) The 2 x 2 grid now applies at every width. The page is never wider than
                max-w-sm, so from sm up the natural-width row couldn't fit four tabs once the count
                badges were added, and "Other" dropped onto a line of its own. */}
            {/* (24 Sep 2026) Moved here from between the tabs and the description box:
                read the dietary needs first, then choose a course, then describe the dish. */}
            <DietaryFlagsBanner dietaryFlags={pollState?.dietaryFlags} />

            <div className="grid grid-cols-2 gap-1 mb-3">
              {tabQuestions.map((q) => {
                const course = q.config.course;
                const filled = offerings[q.id] && offerings[q.id].trim();
                // (23 Sep 2026) How many OTHER members have offered this course (both phases
                // since 24 Sep), shown as a small count on the tab so members can
                // see which courses are covered without opening each tab. Own offer excluded: that's
                // what the amber dot already shows, and counting it too would blur the two.
                const othersCount = alreadyOffered
                  ? (alreadyOffered[course] || []).filter((o) => !o.isMe).length
                  : 0;
                return (
                  <button
                    key={q.id}
                    type="button"
                    onClick={() => setActiveCourse(course)}
                    className={`min-w-0 px-2 py-2 rounded-lg text-sm font-medium text-center border relative ${
                      activeCourse === course
                        ? 'bg-amber-800 border-amber-800 text-white'
                        : 'bg-white border-stone-300 text-stone-700'
                    }`}
                  >
                    {course}
                    {othersCount > 0 && (
                      <span
                        className={`ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-xs font-semibold align-middle ${
                          activeCourse === course ? 'bg-white text-amber-800' : 'bg-stone-200 text-stone-700'
                        }`}
                        title={`${othersCount} offered by other members`}
                        aria-label={`${othersCount} offered by other members`}
                      >
                        {othersCount}
                      </span>
                    )}
                    {filled && (
                      <span className="absolute top-0.5 right-1.5 w-1.5 h-1.5 rounded-full bg-amber-500" />
                    )}
                  </button>
                );
              })}
            </div>

            {tabQuestions
              .filter((q) => q.config.course === activeCourse)
              .map((q) => (
                <div key={q.id}>
                  {/* (24 Sep 2026) Phase 2 too, so a member can see what others are
                      bringing before deciding to change their own offer. */}
                  {alreadyOffered && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-stone-600 mb-1.5">
                        Offerings so far — {activeCourse}
                      </p>
                      <div className="rounded-lg border border-stone-200 bg-stone-50 max-h-32 overflow-y-auto">
                        {!(alreadyOffered[activeCourse] || []).length ? (
                          <p className="text-xs text-stone-400 italic p-3">Nothing offered for this course yet.</p>
                        ) : (
                          <ul className="divide-y divide-stone-200">
                            {alreadyOffered[activeCourse].map((o, i) => (
                              <li
                                key={i}
                                className={`px-3 py-2 text-sm ${o.isMe ? 'bg-amber-50' : ''}`}
                              >
                                <span className={`font-medium ${o.isMe ? 'text-amber-800' : 'text-stone-700'}`}>
                                  {o.isMe ? (proxyMode ? (proxyTargetName || 'Them') : 'You') : o.preferredName}
                                </span>
                                {/* (26 Oct 2026, decision 146) While dishes are a surprise. */}
                                {o.hidden
                                  ? <span className="text-stone-400 italic"> — a surprise</span>
                                  : <span className="text-stone-500"> — {o.description}</span>}
                                <ContainsLine allergens={o.allergens} allergensOther={o.allergensOther} myAllergies={proxyMode ? [] : pollState?.myAllergies} />
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <p className="text-xs text-stone-400 mt-1">
                        {!pollState?.dishesHidden
                          ? 'Check here first to help avoid duplicates.'
                          : (pollState.dishesHiddenFromMe ? HIDDEN_DISHES_NOTE : HIDDEN_DISHES_ADMIN_NOTE)}
                      </p>
                    </div>
                  )}

                  {/* (25 Oct 2026) 16 px text on phones (text-base): below 16 px, iPhone Safari
                      zooms the whole page in when the box is tapped, pushing the page sideways. */}
                  <textarea
                    value={offerings[q.id] || ''}
                    onChange={(e) => setDish(q.id, e.target.value)}
                    placeholder="Brief description:"
                    className="w-full border border-stone-300 rounded-lg p-3 text-base sm:text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  {/* (26 Oct 2026, decision 147) Once there is a dish to tag. */}
                  {offerings[q.id] && offerings[q.id].trim() && (
                    <DishAllergenTicks
                      value={allergenTicks[q.id]}
                      onChange={(v) => setAllergenTicks((prev) => ({ ...prev, [q.id]: v }))}
                    />
                  )}
                </div>
              ))}

            {!isConfirmPhase && myOfferingCount > 0 && (
              <p className="text-xs text-green-700 mt-2">
                {(() => {
                  // One dish_outline question per course, so each named
                  // course contributes at most one filled entry — no
                  // per-course count needed, just "1 main", "1 dessert"
                  // etc. in course order. "Other" stays a separate
                  // trailing clause since it isn't a named dish.
                  const namedParts = dishQuestions
                    .filter((q) => q.config.course !== 'Other' && offerings[q.id] && offerings[q.id].trim())
                    .map((q) => `1 ${q.config.course.toLowerCase()}`);

                  const otherCount = dishQuestions.filter(
                    (q) => q.config.course === 'Other' && offerings[q.id] && offerings[q.id].trim()
                  ).length;
                  const otherPart = otherCount > 0
                    ? 'item(s) under the "other" category'
                    : null;

                  const parts = [...namedParts, ...(otherPart ? [otherPart] : [])];
                  let joined;
                  if (parts.length === 1) joined = parts[0];
                  else if (parts.length === 2) joined = parts.join(' and ');
                  else joined = `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

                  return proxyMode
                    ? `${proxyTargetName || 'This member'}: ${joined} offered so far.` // (23 Sep 2026) was "<name>'re offering"; worded to avoid is/are with a couple's name
                    : `You're offering ${joined} so far.`;
                })()}
              </p>
            )}
          </div>
        )}

        {/* Cooking requirements — confirm phase only, optional request to the host */}
        {cookingReqQuestion && hasAnyDish && (
          <div className="bg-white rounded-xl border border-stone-200 p-4 mt-4">
            <SectionLabel>Cooking requirements</SectionLabel>
            <p className="text-xs text-stone-500 mb-3">
              {/* (25 Sep 2026) Reworded (Neil): "minutes" in full, and the follow-up
                  sentence now says who follows up with whom in each mode. */}
              {proxyMode
                ? "Optional — let the host know what they'll need, e.g. air fryer, microwave, grill for 10 minutes. If what they need is unavailable, the host will follow up with them directly or in the group chat."
                : "Optional — let the host know what you'll need, e.g. air fryer, microwave, grill for 10 minutes. If what you need is unavailable, they'll follow up with you directly or in the group chat."}
            </p>
            <textarea
              value={offerings[cookingReqQuestion.id] || ''}
              onChange={(e) => setDish(cookingReqQuestion.id, e.target.value)}
              placeholder="e.g. microwave, grill, or any other requirement"
              className="w-full border border-stone-300 rounded-lg p-3 text-base sm:text-sm h-16 resize-none focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        )}

        {pollState?.myLastRespondedAt && (
          <p className="text-xs text-stone-500 mt-3 text-center">
            {/* (23 Sep 2026) formatShortDate expects a date-only 'YYYY-MM-DD'
                string (as closesOn is) — a full timestamp gave "NaN Invalid
                Date". Convert to the viewer's LOCAL calendar date first, so
                the date always agrees with the local time shown after it. */}
            {/* (23 Sep 2026) In proxy mode this is the member's reply, not the
                GA's own, so name the member instead of saying "You". */}
            {proxyMode ? `${proxyTargetName || 'This member'} last responded on ` : 'You last responded on '}{(() => {
              const d = new Date(pollState.myLastRespondedAt);
              const pad = (n) => String(n).padStart(2, '0');
              const localDay = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
              return formatShortDate ? formatShortDate(localDay) : localDay;
            })()}
            {' at '}
            {new Date(pollState.myLastRespondedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.
          </p>
        )}

        {needsAttendance && (
          <p className="text-xs text-rose-700 mt-3 text-center">
            {proxyMode
              ? `Please select whether ${proxyTargetName || 'this member'} ${proxyVerb} attending above before saving.`
              : "Please select whether you're attending above before saving."}
          </p>
        )}

        {/* (25 Sep 2026) No longer disabled when attendance is missing:
            handleSave points the member to the attendance question instead. */}
        {/* (26 Sep 2026, H2) After a save — or on opening a poll already
            answered — the button reads "Saved ✓" (Phase 2: "Final answer
            confirmed ✓") in a quieter style until something on the page
            changes. Never disabled: tapping it then just says so. Only when an
            answer to THIS poll exists (myLastRespondedAt), so an unanswered
            poll never claims to be saved. */}
        {(() => {
          const isSavedState = !saving && !isDirty && !!pollState?.myLastRespondedAt;
          return (
            <button
              ref={saveButtonRef}
              type="button"
              onClick={isSavedState
                ? () => status(proxyMode ? 'No changes since this answer was saved.' : 'No changes since your last save.', 'info')
                : handleSave}
              disabled={saving}
              className={`w-full mt-4 rounded-xl py-3 text-sm font-semibold disabled:opacity-50 ${
                isSavedState
                  ? 'bg-stone-100 text-stone-600 border border-stone-300'
                  : 'bg-rose-800 text-white active:bg-rose-900'
              }`}
            >
              {saving
                ? 'Saving…'
                : isSavedState
                  ? (isConfirmPhase ? 'Final answer confirmed ✓' : 'Saved ✓')
                  : isConfirmPhase
                    ? 'Confirm final answer'
                    : (proxyMode ? 'Save answer' : 'Save my answer')}
            </button>
          );
        })()}

        <LiveOfferingsCard live={live} myMemberId={proxyMode ? null : pollState?.memberId} />
      </div>

      {/* (25 Oct 2026, decision 136) Unsaved-changes bar. On a phone the Save button at the
          end of the page is out of sight while typing (Tim & Teri's dish, typed but never
          saved, Phase 1 of November Hot Club). Whenever something on the page differs from
          what was last saved, this bar stays at the bottom of the screen, just above the
          keyboard, with the same Save as the button below. It goes once the answer is saved,
          and is hidden while the "Unsaved changes" dialog is open. */}
      {/* Not while the Save button at the end of the page is itself on screen. */}
      {isDirty && !leavePrompt && !saveButtonVisible && (
        <div
          className="fixed inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-stone-300 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]"
          style={{ bottom: bottomInset, paddingBottom: bottomInset ? 0 : 'env(safe-area-inset-bottom)' }}
        >
          <div className="max-w-sm mx-auto px-4 py-2.5 flex items-center gap-3">
            <p className="flex-1 min-w-0 text-sm font-medium text-amber-800">
              {/* One line on a phone beside "Confirm final answer". */}
              Unsaved changes
            </p>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="shrink-0 rounded-lg px-4 py-2.5 text-sm font-semibold bg-rose-800 text-white active:bg-rose-900 disabled:opacity-50"
            >
              {saving
                ? 'Saving…'
                : isConfirmPhase
                  ? 'Confirm final answer'
                  : (proxyMode ? 'Save answer' : 'Save my answer')}
            </button>
          </div>
        </div>
      )}

      {/* (25 Sep 2026) Unsaved-changes dialog. */}
      {leavePrompt && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-xs p-5">
            <p className="text-base font-semibold text-stone-800 mb-2">Unsaved changes</p>
            {needsAttendance ? (
              <p className="text-sm text-stone-600 mb-4">
                {whoseAnswer} hasn't been saved. Choose {attendanceChoices} first, then save.
              </p>
            ) : (
              <p className="text-sm text-stone-600 mb-4">
                {proxyMode
                  ? `Your changes to ${proxyTargetName || 'this member'}'s answer haven't been saved yet.`
                  : "Your changes haven't been saved yet."}
              </p>
            )}
            <div className="flex flex-col gap-2">
              {needsAttendance ? (
                <button
                  type="button"
                  onClick={leaveStay}
                  className="w-full bg-rose-800 text-white rounded-lg py-2.5 text-sm font-semibold active:bg-rose-900"
                >
                  Stay and finish
                </button>
              ) : (
                <button
                  type="button"
                  onClick={leaveSaveAndGo}
                  disabled={saving}
                  className="w-full bg-rose-800 text-white rounded-lg py-2.5 text-sm font-semibold active:bg-rose-900 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save and leave'}
                </button>
              )}
              <button
                type="button"
                onClick={leaveWithoutSaving}
                disabled={saving}
                className="w-full bg-white border border-stone-300 text-stone-700 rounded-lg py-2.5 text-sm font-medium active:bg-stone-50 disabled:opacity-50"
              >
                Leave without saving
              </button>
              {!needsAttendance && (
                <button
                  type="button"
                  onClick={leaveStay}
                  disabled={saving}
                  className="w-full text-stone-500 rounded-lg py-2 text-sm font-medium disabled:opacity-50"
                >
                  Stay
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <SettingsModal
          isOpen={showSettings}
          onClose={() => { setShowSettings(false); setSettingsInitialTab('profile'); }}
          userEmail={userEmail}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          onStatusMessage={onStatusMessage}
          userTier={userTier}
          initialTab={settingsInitialTab}
          currentCalendar={currentMemberCalendar}
          firstName={firstName}
          lastName={lastName}
          onSaveProfile={onSaveProfile}
          restrictToProfileTabs={true}
        />
      )}
    </div>
  );
}
